/** Harmony + melody generation with simple musical rules (step-wise motion, chord tones on strong beats, resolutions). */
import { mod, type Rng } from '../util';
import { EMPTY, MELODY_CADENCE, chooseRhythm, lastHit, type Pattern } from './patterns';

export interface Chord {
  readonly name: string;
  /** Bass note (MIDI). */
  readonly bass: number;
  /** Pad voicing (MIDI). */
  readonly pad: readonly number[];
  /** Chord-tone scale-degree classes (0..len-1). */
  readonly tones: readonly number[];
  /** Stable resolution degrees (root / fifth). */
  readonly rest: readonly number[];
}

/** Walks through chord progressions, picking a fresh one (never the same twice) at each loop. */
export class Harmony {
  private prog: readonly number[];
  private pos = -1;

  constructor(
    private readonly chords: readonly Chord[],
    private readonly progs: readonly (readonly number[])[],
    private readonly rng: Rng,
  ) {
    this.prog = progs[0] ?? [0];
  }

  restart(): void {
    this.prog = this.progs[0] ?? [0];
    this.pos = -1;
  }

  next(): Chord {
    this.pos++;
    if (this.pos >= this.prog.length) {
      this.pos = 0;
      if (this.progs.length > 1) {
        let p = this.prog;
        for (let i = 0; i < 4 && p === this.prog; i++) p = this.rng.pick(this.progs);
        this.prog = p;
      }
    }
    return this.chords[this.prog[this.pos] ?? 0] ?? (this.chords[0] as Chord);
  }
}

function has(arr: readonly number[], v: number): boolean {
  for (let i = 0; i < arr.length; i++) if (arr[i] === v) return true;
  return false;
}

/** A random walk on scale degrees: mostly steps, a few skips, gravity toward the middle of the range. */
export class MelodyWalker {
  deg: number;

  constructor(
    private readonly len: number,
    readonly lo: number,
    readonly hi: number,
    private readonly rng: Rng,
    start: number,
  ) {
    this.deg = start;
  }

  private isTone(d: number, tones: readonly number[]): boolean {
    return has(tones, mod(d, this.len));
  }

  private fold(d: number): number {
    if (d > this.hi) d = this.hi - (d - this.hi);
    if (d < this.lo) d = this.lo + (this.lo - d);
    return Math.max(this.lo, Math.min(this.hi, d));
  }

  next(tones: readonly number[], strong: boolean): number {
    const r = this.rng.next();
    let move = r < 0.06 ? 0 : r < 0.37 ? 1 : r < 0.68 ? -1 : r < 0.8 ? 2 : r < 0.92 ? -2 : r < 0.96 ? 3 : -3;
    const centre = (this.lo + this.hi) / 2;
    if (this.deg > centre + 2 && move > 0 && this.rng.chance(0.5)) move = -move;
    if (this.deg < centre - 2 && move < 0 && this.rng.chance(0.5)) move = -move;
    let d = this.fold(this.deg + move);
    if (strong && !this.isTone(d, tones)) {
      const dir = move >= 0 ? 1 : -1;
      if (this.isTone(d + dir, tones)) d += dir;
      else if (this.isTone(d - dir, tones)) d -= dir;
      else if (this.isTone(d + 2 * dir, tones)) d += 2 * dir;
      d = this.fold(d);
    }
    this.deg = d;
    return d;
  }

  /** Move to the nearest stable degree (root / fifth class), preferring downward resolution. */
  resolve(rest: readonly number[]): number {
    let best = this.deg;
    let bestDist = Infinity;
    for (let d = this.lo; d <= this.hi; d++) {
      if (!has(rest, mod(d, this.len))) continue;
      const dist = Math.abs(d - this.deg) + (d > this.deg ? 0.25 : 0);
      if (dist < bestDist) {
        bestDist = dist;
        best = d;
      }
    }
    this.deg = best;
    return best;
  }
}

/** One bar at a time: picks a rhythm (with motif repetition), yields degrees per step. */
export class MelodyLine {
  private rhythm: Pattern = EMPTY;
  private last = -1;
  private motif: Pattern | null = null;
  private cadence = false;

  constructor(
    readonly walker: MelodyWalker,
    private readonly rng: Rng,
  ) {}

  /** `rest` = chance of a silent bar (breathing room). */
  bar(density: number, phraseBar: number, rest: number): void {
    const rng = this.rng;
    this.cadence = false;
    if (phraseBar === 3) {
      this.rhythm = rng.pick(MELODY_CADENCE);
      this.cadence = true;
    } else if (phraseBar === 2 && this.motif && rng.chance(0.45)) {
      this.rhythm = this.motif; // call & response: repeat the opening rhythm
    } else if (rng.chance(rest)) {
      this.rhythm = EMPTY;
    } else {
      this.rhythm = chooseRhythm(rng, density);
      if (phraseBar === 0) this.motif = this.rhythm;
    }
    this.last = lastHit(this.rhythm);
  }

  silence(): void {
    this.rhythm = EMPTY;
    this.last = -1;
  }

  /** Degree for step i, or NaN when the step is silent. */
  step(i: number, chord: Chord): number {
    const h = this.rhythm[i];
    if (!h) return NaN;
    if (this.cadence && i === this.last) return this.walker.resolve(chord.rest);
    return this.walker.next(chord.tones, i % 4 === 0 || h === 2);
  }

  /** True when step i is the phrase's final, resolving note. */
  isFinal(i: number): boolean {
    return this.cadence && i === this.last;
  }

  hitAt(i: number): boolean {
    return (this.rhythm[i] ?? 0) > 0;
  }
}
